"use client"

import React from "react"
import styled from "styled-components"
import { motion } from "framer-motion"
import { Add as AddIcon, History as HistoryIcon, Download as DownloadIcon } from "@mui/icons-material"

const FABContainer = styled(motion.div)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 100;
`

const MainButton = styled(motion.button)`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent-blue) 0%, var(--highlight) 100%);
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(79, 141, 251, 0.4);
  
  &:focus {
    outline: none;
  }
`

const ActionButton = styled(motion.button)`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--input-bg);
  color: var(--accent-blue);
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: absolute;
  right: 5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &:focus {
    outline: none;
  }
`

const ActionLabel = styled(motion.div)`
  position: absolute;
  right: 70px;
  background-color: var(--input-bg);
  color: var(--primary-text);
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.9rem;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border: 1px solid var(--border);
`

const FloatingActionButton = ({ onNewQuery, onViewHistory, onDownloadReport, onShare }) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const toggleOpen = () => {
    setIsOpen(!isOpen)
  }

  // Animation variants
  const containerVariants = {
    open: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
    closed: {
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  }

  const mainButtonVariants = {
    hover: {
      scale: 1.1,
      rotate: 180,
      boxShadow: "0 6px 24px rgba(79, 141, 251, 0.6)",
    },
    tap: { scale: 0.95 },
  }

  const actionButtonVariants = {
    open: (i) => ({
      y: i * -65,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    }),
    closed: {
      y: 0,
      opacity: 0,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 30,
      },
    },
    hover: {
      scale: 1.1,
      boxShadow: "0 6px 16px rgba(0, 0, 0, 0.3)",
    },
    tap: { scale: 0.95 },
  }

  const labelVariants = {
    open: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
    closed: {
      opacity: 0,
      x: 20,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 30,
      },
    },
  }

  return (
    <FABContainer variants={containerVariants} initial="closed" animate={isOpen ? "open" : "closed"}>
      {/* New Query Button */}
      <ActionButton
        custom={3}
        variants={actionButtonVariants}
        whileHover="hover"
        whileTap="tap"
        onClick={() => {
          onNewQuery()
          setIsOpen(false)
        }}
      >
        <AddIcon />
      </ActionButton>
      <ActionLabel variants={labelVariants} style={{ top: "calc(-3 * 65px + 15px)" }}>
        New Query
      </ActionLabel>

      {/* History Button */}
      <ActionButton
        custom={2}
        variants={actionButtonVariants}
        whileHover="hover"
        whileTap="tap"
        onClick={() => {
          onViewHistory()
          setIsOpen(false)
        }}
      >
        <HistoryIcon />
      </ActionButton>
      <ActionLabel variants={labelVariants} style={{ top: "calc(-2 * 65px + 15px)" }}>
        View History
      </ActionLabel>

      {/* Download Button */}
      <ActionButton
        custom={1}
        variants={actionButtonVariants}
        whileHover="hover"
        whileTap="tap"
        onClick={() => {
          onDownloadReport()
          setIsOpen(false)
        }}
      >
        <DownloadIcon />
      </ActionButton>
      <ActionLabel variants={labelVariants} style={{ top: "calc(-1 * 65px + 15px)" }}>
        Download Report
      </ActionLabel>

      {/* Main Button */}
      <MainButton variants={mainButtonVariants} whileHover="hover" whileTap="tap" onClick={toggleOpen}>
        {isOpen ? <AddIcon style={{ transform: "rotate(45deg)" }} /> : <AddIcon />}
      </MainButton>
    </FABContainer>
  )
}

export default FloatingActionButton

